import type { AudioObjectV2 } from "@bezpart-board/shared";

export const AUDIO_DEFAULT_WIDTH = 320;
/**
 * Высота карточки. 72 хватало Chrome, но не Safari: там проигрыватель
 * выше (31 px против 21), и его нижний край подрезался. 88 вмещает
 * название, проигрыватель и строку состояния в обоих браузерах.
 */
export const AUDIO_DEFAULT_HEIGHT = 88;
export const AUDIO_MAX_BYTES = 20_971_520;

const MP3_MIMES = new Set(["audio/mpeg", "audio/mp3", "audio/x-mpeg", "audio/x-mp3"]);
const VOICE_MIMES = new Set([
  "audio/webm",
  "audio/ogg",
  "audio/wav",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
]);

function audioMimeBase(mime: string): string {
  return mime.trim().toLowerCase().split(";")[0]!.trim();
}

export function isMp3File(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".mp3")) return true;
  return MP3_MIMES.has(audioMimeBase(file.type));
}

/** MP3 и голосовые записи (webm/ogg/wav/m4a). */
export function isBoardAudioFile(file: File): boolean {
  if (isMp3File(file)) return true;
  const name = file.name.toLowerCase();
  if (/\.(webm|ogg|oga|wav|m4a|mp4)$/i.test(name)) return true;
  const t = audioMimeBase(file.type);
  return VOICE_MIMES.has(t);
}

function resolveAudioUploadMime(file: File): string {
  const raw = audioMimeBase(file.type);
  if (MP3_MIMES.has(raw)) return "audio/mpeg";
  if (VOICE_MIMES.has(raw)) return raw === "audio/x-m4a" ? "audio/mp4" : raw;

  const n = file.name.toLowerCase();
  if (n.endsWith(".mp3")) return "audio/mpeg";
  if (n.endsWith(".webm")) return "audio/webm";
  if (n.endsWith(".ogg") || n.endsWith(".oga")) return "audio/ogg";
  if (n.endsWith(".wav")) return "audio/wav";
  if (n.endsWith(".m4a") || n.endsWith(".mp4")) return "audio/mp4";
  return "audio/mpeg";
}

function extForAudioMime(mime: string): string {
  if (mime === "audio/mpeg") return ".mp3";
  if (mime === "audio/webm") return ".webm";
  if (mime === "audio/ogg") return ".ogg";
  if (mime === "audio/wav") return ".wav";
  if (mime === "audio/mp4" || mime === "audio/x-m4a" || mime === "audio/aac") return ".m4a";
  return ".mp3";
}

function ensureAudioFilename(file: File, mime: string): string {
  const name = file.name?.trim();
  if (name && /\.(mp3|webm|ogg|oga|wav|m4a|mp4)$/i.test(name)) return name;
  const base = (name || "audio").replace(/\.[^.]+$/, "") || "audio";
  return `${base}${extForAudioMime(mime)}`;
}

export type ImportAudioOptions = {
  file: File;
  boardId: string;
  shareToken?: string | null;
  guestEditKey?: string | null;
  useCredentials: boolean;
  worldX: number;
  worldY: number;
};

type UploadedAsset = { id: string };

async function readUploadError(res: Response): Promise<string> {
  const text = await res.text();
  if (!text) return `Upload failed (${res.status})`;
  try {
    const data = JSON.parse(text) as { error?: string; message?: string };
    return data.error || data.message || `Upload failed (${res.status})`;
  } catch {
    if (res.status === 413) return "Файл слишком большой для сервера";
    if (res.status === 502 || res.status === 504) return "Сервер не принял файл (ошибка шлюза)";
    if (text.trimStart().startsWith("<")) {
      return `Сервер вернул ошибку вместо JSON (${res.status})`;
    }
    return text.slice(0, 160) || `Upload failed (${res.status})`;
  }
}

async function uploadAsset(opts: ImportAudioOptions): Promise<UploadedAsset> {
  const mime = resolveAudioUploadMime(opts.file);
  const form = new FormData();
  // Явный MIME — иначе часть браузеров шлёт пустой type / octet-stream
  const blob = new Blob([opts.file], { type: mime });
  const filename = ensureAudioFilename(opts.file, mime);
  form.append("file", blob, filename);
  form.append("boardId", opts.boardId);
  if (opts.shareToken) form.append("shareToken", opts.shareToken);

  let url = "/api/v1/assets/upload";
  if (opts.shareToken) url += `?t=${encodeURIComponent(opts.shareToken)}`;
  else if (opts.guestEditKey) url += `?k=${encodeURIComponent(opts.guestEditKey)}`;

  const res = await fetch(url, {
    method: "POST",
    credentials: opts.useCredentials ? "include" : "omit",
    body: form,
  });

  if (!res.ok) {
    throw new Error(await readUploadError(res));
  }

  const text = await res.text();
  let data: { asset?: UploadedAsset; error?: string };
  try {
    data = JSON.parse(text) as { asset?: UploadedAsset; error?: string };
  } catch {
    throw new Error("Сервер вернул не JSON при загрузке аудио");
  }
  if (!data.asset?.id) throw new Error(data.error || "Upload failed");
  return data.asset;
}

export async function importAudioToBoard(opts: ImportAudioOptions): Promise<AudioObjectV2> {
  if (!isBoardAudioFile(opts.file)) {
    throw new Error("Поддерживаются MP3 и голосовые записи (webm/ogg/wav/m4a)");
  }
  if (opts.file.size > AUDIO_MAX_BYTES) {
    throw new Error("Аудио слишком большое (макс. 20 МБ)");
  }

  const asset = await uploadAsset(opts);
  const title =
    opts.file.name.replace(/\.(mp3|webm|ogg|oga|wav|m4a|mp4)$/i, "").trim() || "Аудио";
  const w = AUDIO_DEFAULT_WIDTH;
  const h = AUDIO_DEFAULT_HEIGHT;

  return {
    id: crypto.randomUUID(),
    type: "audio",
    x: Math.round(opts.worldX - w / 2),
    y: Math.round(opts.worldY - h / 2),
    w,
    h,
    assetId: asset.id,
    title,
    listenTogether: false,
  };
}
